import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Tabs,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  message,
  Checkbox,
  Descriptions,
  Tag,
} from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { generateCodeFromName } from "../../../utils/codeUtils";
import BrandsTab from "./BrandsTab";
import SizesTab from "./SizesTab";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const ItemDetailsDrawer = ({
  visible,
  onClose,
  item,
  onUpdate,
  categories,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (visible && item) {
      form.setFieldsValue({
        category: item.category,
        name: item.name,
        item_type: item.item_type || "Required",
        has_size: item.has_size || false,
        has_brands: item.has_brands || false,
        description: item.description || "",
        is_active: item.is_active === 1,
      });
      setEditMode(false);
      setActiveTab("1");
    }
  }, [visible, item, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const normalizedName = values.name?.trim() || "";
      const shouldKeepExistingCode =
        normalizedName === (item.name || "").trim();
      const generatedCode = generateCodeFromName(normalizedName);
      const finalCode =
        shouldKeepExistingCode && item?.code
          ? item.code
          : generatedCode || item?.code || "";

      const payload = {
        category: values.category,
        name: values.name,
        code: finalCode,
        item_type: values.item_type,
        has_size: values.has_size,
        has_brands: values.has_brands,
        description: values.description || null,
        is_active: values.is_active ? 1 : 0,
      };

      const response = await Utilities.fetchWithAuth(
        "PUT",
        domainSettings.urls.bot.itemDetail.replace(":id", item.id),
        payload,
        navigate,
      );

      if (response.ok) {
        message.success("Item updated successfully");
        setEditMode(false);
        onUpdate();
        onClose(); // Close drawer to show updated items list
      } else {
        Utilities.showError(response, "Failed to update item");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  return (
    <Drawer
      title={
        <Space>
          <span>{item.name}</span>
          <Tag color={item.is_active === 1 ? "green" : "red"}>
            {item.is_active === 1 ? "Active" : "Inactive"}
          </Tag>
        </Space>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={window.innerWidth > 768 ? 800 : "100%"}
      extra={
        <Space>
          {editMode ? (
            <>
              <Button
                onClick={() => setEditMode(false)}
                icon={<CloseOutlined />}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                loading={loading}
                icon={<SaveOutlined />}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <Button type="primary" onClick={() => setEditMode(true)}>
              Edit Item
            </Button>
          )}
        </Space>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Details" key="1">
          {editMode ? (
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                label="Category"
                name="category"
                rules={[
                  { required: true, message: "Please select a category" },
                ]}
              >
                <Select placeholder="Select category">
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Item Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter item name" },
                  { max: 200, message: "Name cannot exceed 200 characters" },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Item Type"
                name="item_type"
                rules={[{ required: true, message: "Please select item type" }]}
              >
                <Select>
                  <Option value="Required">Required</Option>
                  <Option value="Optional">Optional</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Item Properties">
                <Form.Item name="has_size" valuePropName="checked" noStyle>
                  <Checkbox>This item has size variants</Checkbox>
                </Form.Item>
                <br />
                <Form.Item name="has_brands" valuePropName="checked" noStyle>
                  <Checkbox>This item has multiple brands</Checkbox>
                </Form.Item>
              </Form.Item>

              <Form.Item label="Description" name="description">
                <TextArea rows={4} />
              </Form.Item>

              <Form.Item
                label="Active"
                name="is_active"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Form>
          ) : (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Item Name">
                {item.name}
              </Descriptions.Item>
              <Descriptions.Item label="Item Code">
                {item.code}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {item.category_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Item Type">
                <Tag color={item.item_type === "Required" ? "red" : "blue"}>
                  {item.item_type || "Optional"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Has Size Variants">
                {item.has_size ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Has Brands">
                {item.has_brands ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {item.description || "No description"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={item.is_active === 1 ? "green" : "red"}>
                  {item.is_active === 1 ? "Active" : "Inactive"}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          )}
        </TabPane>

        <TabPane tab="Brands" key="2" disabled={!item.has_brands}>
          {item.has_brands ? (
            <BrandsTab itemId={item.id} />
          ) : (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#999" }}
            >
              This item does not have brands enabled.
              <br />
              Enable brands in the Details tab to manage them.
            </div>
          )}
        </TabPane>

        <TabPane tab="Sizes" key="3" disabled={!item.has_size}>
          {item.has_size ? (
            <SizesTab itemId={item.id} />
          ) : (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#999" }}
            >
              This item does not have size variants enabled.
              <br />
              Enable size variants in the Details tab to manage them.
            </div>
          )}
        </TabPane>
      </Tabs>
    </Drawer>
  );
};

export default ItemDetailsDrawer;
